import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface Props {
  userId: number | null | undefined;
}

/**
 * Bandeau « Site fermé » du tableau de bord marchand.
 * S'affiche uniquement quand le commerce a désactivé son site internet,
 * avec un bouton de réactivation immédiate.
 */
export default function SiteClosedBanner({ userId }: Props) {
  const [closed, setClosed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('users')
          .select('site_enabled')
          .eq('id', userId)
          .maybeSingle();
        if (!cancelled) setClosed(data?.site_enabled === false);
      } catch {
        /* silencieux : le bandeau reste masqué */
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const reactivate = async () => {
    if (!userId) return;
    setBusy(true);
    setError('');
    try {
      const { error: err } = await supabase
        .from('users')
        .update({ site_enabled: true })
        .eq('id', userId);
      if (err) throw err;
      setClosed(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Réactivation impossible.');
    } finally {
      setBusy(false);
    }
  };

  if (!closed) return null;

  return (
    <div className="mb-5 flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 rounded-lg border border-red-200 bg-red-50">
      <span className="w-10 h-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
        <i className="ri-eye-off-line text-lg"></i>
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-bold text-red-700">Site fermé</p>
          <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase tracking-wide">
            Hors ligne
          </span>
        </div>
        <p className="text-xs text-foreground-600 mt-0.5">
          Votre boutique n&apos;est pas accessible : les visiteurs sont renvoyés vers la page de connexion.
          {error && <span className="text-red-600"> {error}</span>}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={() => void reactivate()}
          disabled={busy}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-600 text-white text-xs font-semibold whitespace-nowrap hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-60"
        >
          {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-restart-line"></i>}
          Réactiver mon site
        </button>
        <Link
          to="/dashboard/settings?tab=site"
          className="px-4 py-2 rounded-full bg-background-50 border border-background-200/70 text-xs font-medium text-foreground-700 whitespace-nowrap hover:bg-background-100 transition-colors no-underline"
        >
          Réglages
        </Link>
      </div>
    </div>
  );
}